'use client';

import React from 'react';
import clsx from 'clsx';
import { getCurrentUrl } from '@pixum/auth-core-ui';
import Text, { TEXT_VARIANT } from '@pixum/text';
import './styles.css';
import type { HotspotContentProps } from './types';
import { JENTIS_PENDING_IDENTIFIER, redirectToWithPageReload, SessionStorage } from 'utils';
import RatingStars from '../rating/RatingStars';
import { RatingCaption } from '../rating';

const HOTSPOT_DESCRIPTION_TRACKING_CATEGORY = 'hot_spot_description';

/**
 * Renders the tooltip content for a hotspot, including title, description, rating,
 * and price, based on visibility state. Handles click tracking and redirection.
 *
 * @param props - The component props.
 * @param props.title - The title to display in the tooltip.
 * @param [props.description] - Optional description below the title.
 * @param [props.hideDescription] - Flag to hide the description.
 * @param [props.productReviews] - Optional rating data (average and total count).
 * @param [props.hideRating] - Flag to hide the rating section.
 * @param [props.price] - Price information to be displayed.
 * @param [props.hidePrice] - Flag to hide the price.
 * @param props.isVisible - Whether the tooltip is currently visible.
 * @param [props.link] - URL to redirect on click.
 * @param {(el: HTMLElement | null) => void} props.setFloating - Ref callback to position floating UI.
 * @param props.floatingStyles - Styles computed for tooltip positioning.
 * @param props.index - Index used for tooltip ID and animation delay.
 * @returns The rendered tooltip content for a hotspot.
 */
export function HotspotContent(props: HotspotContentProps) {
  const {
    title,
    description,
    hideDescription,
    productReviews,
    hideRating,
    priceFormats,
    hidePrice,
    isVisible,
    link,
    setFloating,
    floatingStyles,
    index,
    constants,
  } = props;
  const currentUrl = getCurrentUrl();
  const hotspotId = `hotspot-item-${(index || 0) + 1}`;
  const itemChildClasses = clsx('hotspot-item-child', {
    'hotspot-item-child-visible': isVisible,
    'hotspot-item-child-hidden': !isVisible,
  });

  /**
   * Set tracking data in the session storage to fetch when the redirect happened. We solve in this way losing the tracking
   * when page is redirected
   */
  const handleClick = () => {
    if (link) {
      const trackingData = {
        module: HOTSPOT_DESCRIPTION_TRACKING_CATEGORY,
        target: currentUrl,
      };

      SessionStorage.setItem(JENTIS_PENDING_IDENTIFIER, JSON.stringify(trackingData));

      redirectToWithPageReload(link);
    }
  };

  const formattedPriceWithAsterisk = priceFormats?.withAsterisk;

  return (
    <div
      aria-hidden={!isVisible}
      className={itemChildClasses}
      id={hotspotId}
      onClick={handleClick}
      ref={setFloating}
      role="tooltip"
      style={floatingStyles}
    >
      <Text variant={TEXT_VARIANT.HEADLINE}>{title}</Text>

      {!hideDescription && description && <Text variant={TEXT_VARIANT.SUBHEADLINE}>{description}</Text>}
      {!hideRating && productReviews && (
        <div className="hotspot-item--rating">
          <RatingStars value={productReviews?.rating} />{' '}
          <RatingCaption
            count={productReviews?.count}
            rating={productReviews?.rating}
            constant={constants?.i18n_ratings_count as string}
          />
        </div>
      )}
      {!hidePrice && formattedPriceWithAsterisk && constants && (
        <Text className="hotspot-item--price" variant={TEXT_VARIANT.SUBHEADLINE}>
          {constants.i18n_up as string} {formattedPriceWithAsterisk}
        </Text>
      )}
    </div>
  );
}

export default HotspotContent;
